<!--
  FileElement.vue

  Removable chip for a single asset on `attachment` junction fields (the
  attachment counterpart to RecordElement / TagElement): a small preview and
  the file name. Clicking it opens the file in the viewer.

  Mirrors RecordElement / TagElement: `value` is the target entity, `readonly`
  hides the delete button, `size` scales the chip, `fixed` makes it inert (used
  in dropdown suggestions, where a click picks the file).
-->
<template>
  <span
    v-if="value"
    class="tag file-element is-light"
    :class="[size !== 'normal' ? `is-${size}` : null, { 'is-fixed': fixed }]"
    :title="value.name ?? undefined"
    @click="openViewer"
  >
    <FileThumb :asset="value" fluid />
    <span class="name is-elided">{{ value.name || "Untitled" }}</span>
    <button
      v-if="!readonly"
      class="delete"
      :class="size !== 'normal' ? `is-${size}` : null"
      aria-label="Remove file"
      @click.stop="emit('remove', value)"
    ></button>
  </span>
</template>

<script setup lang="ts">
import type { AssetFieldsFragment } from "@auger/core/gql/types"
import FileThumb from "@/components/files/FileThumb.vue"
import { useFileViewer } from "@/composables/useFileViewer"

interface Props {
  value: AssetFieldsFragment
  readonly?: boolean
  size?: string
  fixed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  size: "normal",
  fixed: false,
})

const emit = defineEmits<{
  remove: [asset: AssetFieldsFragment]
}>()

const viewer = useFileViewer()

function openViewer(event: MouseEvent) {
  if (props.fixed || !props.value.url) return
  event.stopPropagation()
  viewer.open([props.value])
}
</script>

<style>
.file-element {
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  max-width: 16rem;
  padding-left: 0.2rem;
  cursor: pointer;

  &.is-fixed {
    cursor: inherit;
  }

  .file-thumb {
    width: 18px;
    height: 18px;
    border-radius: var(--auger-radius-sm);
  }

  &.is-small .file-thumb {
    width: 16px;
    height: 16px;
  }

  .name {
    min-width: 0;
  }
}
</style>
